import { BaseService } from "../../core/baseService";
import {
  QrCodeServiceInterface,
  GetActiveQrCodesParams,
  GenerateQrCodeParams,
  CheckOverlapParams,
  QrTokenOverlapCheckDto,
  ResolveOverlapRequest,
} from "../types/qrCode.types";
import { ActiveQrTokenDto, PaginatedResponse } from "../../../models";

export class QrCodeService
  extends BaseService
  implements QrCodeServiceInterface
{
  async getActiveQrCodes(params: GetActiveQrCodesParams) {
    const { page, pageSize, roomNumber, sortBy, sortOrder } = params;

    const queryParams: Record<string, unknown> = {
      pageNumber: page, // Note: API uses pageNumber, not page
      pageSize,
      sortBy: sortBy || "ExpiresAtUtc",
      sortOrder: sortOrder || "asc",
    };

    if (roomNumber) {
      queryParams.roomNumber = roomNumber;
    }

    return this.handleRequest<PaginatedResponse<ActiveQrTokenDto>>(
      this.client.get("/api/admin/qr-code/active-codes", {
        params: queryParams,
      }),
    );
  }

  async generateQrCode(roomId: string, params: GenerateQrCodeParams) {
    const {
      checkInDate,
      checkoutDate,
      generateDigitalKey,
      guestName,
      createStay,
      guestId,
      guestFirstName,
      guestLastName,
      guestEmail,
      guestPhoneNumber,
      guestPhoneCountryCode,
      guestDocumentType,
      guestDocumentNumber,
      guestNationality,
      adults,
      children,
      bookingReference,
      specialRequests,
      roomRate,
      currency,
    } = params;

    // Backend expects PascalCase
    const payload: Record<string, unknown> = {
      CheckInDate: checkInDate,
      CheckoutDate: checkoutDate,
      GenerateDigitalKey: generateDigitalKey,
      GuestName: guestName,
      CreateStay: createStay,
      RegenerateTokenOnly: params.regenerateTokenOnly,
      GuestId: guestId,
      GuestFirstName: guestFirstName,
      GuestLastName: guestLastName,
      GuestEmail: guestEmail,
      GuestPhoneNumber: guestPhoneNumber,
      GuestPhoneCountryCode: guestPhoneCountryCode,
      GuestDocumentType: guestDocumentType,
      GuestDocumentNumber: guestDocumentNumber,
      GuestNationality: guestNationality,
      Adults: adults,
      Children: children,
      BookingReference: bookingReference,
      SpecialRequests: specialRequests,
      RoomRate: roomRate,
      Currency: currency,
    };

    // Remove undefined values
    Object.keys(payload).forEach((key) => {
      if (payload[key] === undefined) {
        delete payload[key];
      }
    });

    return this.handleRequest<ActiveQrTokenDto>(
      this.client.post(`/api/admin/qr-code/${roomId}/rotate-token`, payload),
    );
  }

  async checkOverlap(roomId: string, params: CheckOverlapParams) {
    const payload = {
      CheckInDate: params.checkInDate,
      CheckoutDate: params.checkoutDate,
    };

    return this.handleRequest<QrTokenOverlapCheckDto>(
      this.client.post(`/api/admin/qr-code/${roomId}/check-overlap`, payload),
    );
  }

  async generateQrCodeWithResolution(
    roomId: string,
    tokenRequest: GenerateQrCodeParams,
    resolution: ResolveOverlapRequest,
  ) {
    const tokenPayload: Record<string, unknown> = {
      CheckInDate: tokenRequest.checkInDate,
      CheckoutDate: tokenRequest.checkoutDate,
      GenerateDigitalKey: tokenRequest.generateDigitalKey,
      GuestName: tokenRequest.guestName,
      CreateStay: tokenRequest.createStay,
      GuestId: tokenRequest.guestId,
      GuestFirstName: tokenRequest.guestFirstName,
      GuestLastName: tokenRequest.guestLastName,
      GuestEmail: tokenRequest.guestEmail,
      GuestPhoneNumber: tokenRequest.guestPhoneNumber,
      GuestPhoneCountryCode: tokenRequest.guestPhoneCountryCode,
      GuestDocumentType: tokenRequest.guestDocumentType,
      GuestDocumentNumber: tokenRequest.guestDocumentNumber,
      GuestNationality: tokenRequest.guestNationality,
      Adults: tokenRequest.adults,
      Children: tokenRequest.children,
      BookingReference: tokenRequest.bookingReference,
      SpecialRequests: tokenRequest.specialRequests,
      RoomRate: tokenRequest.roomRate,
      Currency: tokenRequest.currency,
    };

    // Remove undefined values
    Object.keys(tokenPayload).forEach((key) => {
      if (tokenPayload[key] === undefined) {
        delete tokenPayload[key];
      }
    });

    const payload = {
      TokenRequest: tokenPayload,
      Resolution: {
        Resolution: resolution.resolution,
        NewCheckOutDate: resolution.newCheckOutDate,
        EarlyCheckOutDate: resolution.earlyCheckOutDate,
        ConfirmOverride: resolution.confirmOverride,
      },
    };

    return this.handleRequest<ActiveQrTokenDto>(
      this.client.post(
        `/api/admin/qr-code/${roomId}/rotate-token-with-resolution`,
        payload,
      ),
    );
  }
}
